import 'vue-awesome/icons/instagram';
import _ from 'lodash';
import Debug from '@/util/debug';
import moment from 'moment-timezone';
import FormInput from '@/components/form-input/FormInput.vue';
import FacebookConnector from '@/components/facebook-connector/FacebookConnector.vue';
import FacebookPageCreation from '@/components/facebook-page-creation/FacebookPageCreation.vue';
import { STATUS_FLOW_ASAP, STATUS_FLOW_DEFAULT, CREATIVE_FLOW_CLASS_ONBOARDING } from '@/services/StatusFlowService';
import FileUpload from '@/components/file-upload/FileUpload.vue';
import CorrectDatepicker from '@/components/correct-datepicker/CorrectDatepicker.vue';
import DateTimePicker from '@/components/datetimepicker/DateTimePicker.vue';
import { PAGE_FACEBOOK } from '@/services/LandingPageService';
import Switches from 'vue-switches';
import Loading from '@/components/loading/Loading.vue';
import { localDateToApi } from '@/util/helper';
import { STEP_SETTINGS, STEP_CHECKOUT } from '@/services/StepService';
import KeyCombo from '@/directives/key-combo';
import { withParams } from 'vuelidate/lib';
import { GOAL_LEAD_GENERATION, GOAL_LOCAL_OFFERS, GOAL_REACH_AND_CLICK, GOAL_POST_ENGAGEMENT } from '@/services/GoalTypeService';
import { isIterationDate, getSubscriptionPeriodType, getSubscriptionPeriodLength } from '../../models/campaign';

const debug = Debug('campaign-settings');
const { required, maxLength, minLength, email, requiredIf, alphaNum, numeric } = require('vuelidate/lib/validators');

const validateUrl = function validateUrl(value) {
  const expression = /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-zA-Z0-9]+([-.]{1}[a-zA-Z0-9]+)*\.[a-zA-Z]{2,}(:[0-9]{1,5})?(\/.*)?$/;
  if (this) {
    this.invalidUrl = false;
  }
  return Boolean(value && value.match(expression) !== null);
};

const checkIfAppstore = function checkIfAppstore(value) {
  const url = this.campaign.landing_page.url;
  if (url && (url.includes('play.google.com') || url.includes('itunes.apple.com'))) {
    return Boolean(value && false);
  }
  return Boolean(value && true);
};

const notFacebookPage = function isFacebookPage(value) {
  const expression = /(facebook.com)/g;
  if (this) {
    this.invalidUrl = false;
  }
  return Boolean(value && value.match(expression) == null);
};

const validateEmail = function validateEmail(value) {
  const expression = /(^$|^(([^<>()[\]\\.,;:\s@"]{0,64}(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$)/;
  return Boolean(value && value.match(expression) !== null);
};

const validatePhoneNr = function validatePhoneNr(value) {
  debug.info('validatePhoneNr %o %o', value, value && value.match(/^\+?[0-9\s]+$/));
  return Boolean(!value || value.match(/^\+?[0-9\s]+$/));
};

const validateCommaSeparatedEmails = function validateCommaSeparatedEmails(value) {
  return !value || value
    .split(',')
    .map(mail => mail.trim())
    .map(mail => validateEmail(mail))
    .every(valid => valid);
};

export default {
  dependencies: [
    'draftService',
    'landingPageService',
    'statusFlowService',
    'statusesService',
    'genericConfigService',
    'facebookPageService',
    'objectivesService',
    'adApiService',
    'goalTypeService',
    'localeService',
  ],
  props: {
    campaign: Object,
  },
  components: {
    'correct-datepicker': CorrectDatepicker,
    datetimepicker: DateTimePicker,
    'form-input': FormInput,
    'facebook-connector': FacebookConnector,
    'file-upload': FileUpload,
    'facebook-page-creation': FacebookPageCreation,
    Switches,
    loading: Loading,
  },
  directives: [
    KeyCombo,
  ],
  data() {
    const phone = this.campaign.customer.phone;
    const salesRepPhone = this.campaign.details.salesRepPhone;
    const leadPhone = this.campaign.details.leadPhone;

    let phonePrefix = phone ? (phone.split(' ')[0] || null) : '';
    let phoneNr = phone ? phone.split(' ').slice(1).join(' ') : '';

    if (phoneNr === '' && phonePrefix && phonePrefix.length > 3) {
      phoneNr = phonePrefix;
      phonePrefix = null;
    }

    return {
      objectiveTemp: null,
      validationUrls: [],
      invalidUrl: false,
      couponEnabled: false,
      loading: false,
      error: null,
      STATUS_FLOW_ASAP,
      STATUS_FLOW_DEFAULT,
      statusFlows: [],
      minStart: moment(),
      maxStart: moment().add(180, 'days'),
      disableFacebookEmail: false,
      pageTypes: [],
      formErrors: [],
      objectives: [],
      availablePhonePrefixes: [],
      datepickerOpened: false,
      fieldSettings: {},
      asapCalculationType: null,
      customerPhone: phoneNr,
      customerPhonePrefix: phonePrefix,
      salesRepPhone: salesRepPhone ? salesRepPhone.split(' ').slice(1).join(' ') : '',
      leadPhone: leadPhone ? leadPhone.split(' ').slice(1).join(' ') : '',
      leadEmail: _.get(this.campaign, 'details.leadEmail', ''),
      leadPrivacyLink: _.get(this.campaign, 'details.leadPrivacyLink', ''),
      offersCodeTypeOptions: [],
      offersRedeemOptions: [],
      minOfferStartDisabled: moment().add(4, 'day').toISOString(),
      maxOfferEnd: moment().add(1, 'year').toISOString(),
      offerRedeemUrlVisible: this.checkRedeemUrlVisible(),
      offerCodeVisible: this.checkCodeVisible(),
      offerSettingSaved: false,
      maxLengthFields: {
        offer_settings: {
          title: 50,
          details: 250,
          t_c: 5000,
          code: 20,
        },
      },
      offerModalErrors: {
        url: [],
        code_type: false,
        redeem_options: false,
        unique_code: false,
      },
      lead_config: null,
      availableLanguages: this.localeService.getLanguages(),
      selectedLanguage: null,
      subscriptionPeriodType: 'months',
      subscriptionPeriodLength: 1,
      replaceFieldExpressions: {},
      disabledFbLandingPageGoalTypes: [
        GOAL_LOCAL_OFFERS,
        GOAL_REACH_AND_CLICK,
      ],
      agent_summary_groups: [],
    };
  },
  validations() {
    const rules = {
      campaign: {
        name: { maxLength: maxLength(150) },
        start_date: { required: requiredIf(() => this.campaign.flow === this.STATUS_FLOW_DEFAULT) },
        landing_page: {
          url: { maxLength: maxLength(250), isUrl: validateUrl, isAppstore: checkIfAppstore },
        },
        objective: { required: requiredIf(() => (this.isFieldVisible('campaign.objective') && !this.localOffersEnabled)) },
        brief: { maxLength: maxLength(2000) },
        customer: {
          name: { maxLength: maxLength(150), required },
          email: { maxLength: maxLength(150), isEmail: validateEmail },
          phone: { maxLength: this.validatePhoneMaxLength(20), isPhoneNr: validatePhoneNr },
          remote_id: { maxLength: maxLength(150) },
        },
        facebook: {
          page: { required: requiredIf(() => this.campaign.facebook.hasFacebookPage) },
          email: { email, maxLength: maxLength(150) },
        },
        local_advertising_address: { },
        details: {
          alliedCoupon: {
            maxLength: maxLength(20), alphaNum, required: requiredIf(() => this.couponEnabled),
          },
          salesRepPhone: {
            maxLength: this.validatePhoneMaxLength(20), isPhoneNr: validatePhoneNr,
          },
          leadPhone: {
            maxLength: this.validatePhoneMaxLength(20), isPhoneNr: validatePhoneNr,
          },
          leadEmail: { validateCommaSeparatedEmails, maxLength: maxLength(255) },
          leadPrivacyLink: {
            required: requiredIf(() => this.leadPrivacyPolicyLink()
              && !_.get(this.campaign, 'create_landing_page', false)),
            isUrl: this.isPrivacyUrlIsValid,
          },
        },
        offer_settings: {
          title: { maxLength: maxLength(this.maxLengthFields.offer_settings.title) },
          details: { maxLength: maxLength(this.maxLengthFields.offer_settings.details) },
          t_c: { maxLength: maxLength(this.maxLengthFields.offer_settings.t_c) },
          code_type: {
            required: requiredIf(() => this.localOffersEnabled),
            not: () => (!this.localOffersEnabled || this.campaign.offer_settings.code_type !== 'empty'),
          },
          redeem: {
            required: requiredIf(() => this.localOffersEnabled),
            not: () => (!this.localOffersEnabled || this.campaign.offer_settings.redeem !== 'empty'),
          },
        },
      },
    };

    this.recursive(rules.campaign, '');

    if (this.localOffersEnabled) {
      delete rules.campaign.objective;
    }

    if ((_.includes(this.disabledFbLandingPageGoalTypes, _.get(this.campaign, 'goal_type')) || this.campaign.type === 'carousel') &&
      !this.campaign.create_landing_page
    ) {
      rules.campaign.landing_page.url = Object.assign(
        {},
        rules.campaign.landing_page.url,
        { isFb: notFacebookPage },
      );
    }

    if (this.isPostEngagement || this.campaign.create_landing_page) {
      delete rules.campaign.landing_page.url;
      delete rules.campaign.landing_page;
    }

    return rules;
  },
  computed: {
    columnCount() {
      return _.size(this.columnGroups);
    },
    columnGroups() {
      let data = this.agent_summary_groups;
      if (this.agent_summary_groups.length) {
        data = JSON.parse(data).filter((el) => Object.values(el).length > 0);
      }
      return data;
    },
    couponCode: {
      get() {
        return this.campaign.details.alliedCoupon || '';
      },
      set(value) {
        const newDetails = {
          ...this.campaign.details,
          alliedCoupon: value,
        };
        if (!value) {
          delete newDetails.alliedCoupon;
        }
        this.campaign.details = newDetails;
        return value;
      },
    },
    formGroups() {
      return {
        customer: {
          'campaign.customer.name': null,
          'campaign.customer.remote_id': null,
          'campaign.customer.email': null,
          'campaign.customer.phone': null,
          'campaign.customer.language': this.multilanguageEnabled,
        },
        campaign_salesrep_details: {
          'campaign.details.salesrep.phone': null,
        },
        campaign_address: {
          'campaign.local_advertising_address': null,
        },
        campaign_facebook: {
          'campaign.facebook.hasFacebookPage': null,
          'campaign.facebook.page': this.campaign.facebook.hasFacebookPage,
          'campaign.facebook.email': null,
        },
        campaign_lead_details: {
          'campaign.details.leadEmail': null,
          'campaign.details.leadPhone': null,
        },
        campaign_base: {
          'campaign.name': null,
          'campaign.start_date': null,
        },
        campaign_setup: {
          'campaign.brief': null,
          'campaign.objective': !this.localOffersEnabled,
          'campaign.landing_page.url': null,
          'campaign.images': null,
        },
        campaign_details: {
          'campaign.details.notify_customer': null,
          'campaign.details.appointed_onboarding': null,
        },
        allied: {
          'campaign.details.alliedCoupon': null,
        },
      };
    },
    isClicksCampaign() {
      return _.get(this.campaign, 'goal_type') === GOAL_REACH_AND_CLICK;
    },
    isPageFacebook() {
      return (_.includes(this.disabledFbLandingPageGoalTypes, _.get(this.campaign, 'goal_type')) || this.campaign.type === 'carousel') && !this.campaign.create_landing_page;
    },
    isPostEngagement() {
      return _.get(this.campaign, 'goal_type') === GOAL_POST_ENGAGEMENT;
    },
    landingPageErrors() {
      if (this.campaign.create_landing_page || this.isPostEngagement) {
        return [];
      }
      return this.$v.campaign.landing_page.url;
    },
    localOffersEnabled() {
      return this.campaign.goal_type === GOAL_LOCAL_OFFERS;
    },
    landingPagePlaceholder() {
      return this.campaign.landing_page.type === 'webpage'
        ? this.$t('draft_field.placeholder.web-page')
        : this.$t('draft_field.placeholder.landing-page');
    },
    predictedStartDate() {
      if (_.get(this.campaign, 'details.clone-of', null) === null && this.asapCalculationType === 'first-day-of-month') {
        const now = moment();
        const monthsToAdd = now.date() > 20 ? 2 : 1;
        return now.add(monthsToAdd, 'months').set('date', 1);
      }
      return this.minStart;
    },
    landingPageTypes() {
      let options = this.pageTypes.map(t => {
        const item = {
          text: this.$t('pos.settings.landing-page-type', { type: t.value }),
          value: t.value,
        };

        if (!this.campaign.facebook.hasFacebookPage && t.value === 'facebookpage') {
          item.disabled = true;
        }

        return item;
      });

      options = options.filter((item) => {
        if (item.value === 'facebookpage') {
          return !_.includes(this.disabledFbLandingPageGoalTypes, _.get(this.campaign, 'goal_type'));
        }

        return true;
      });

      return options;
    },
    availableObjectives() {
      return this.objectives.map(o => ({
        value: o.slug,
        text: `${(o.icon !== null) ? `<image src="${o.icon}"/>` : '<div class="no-image">'}${this.$t(`terms.objective-texts.${o.slug}.name`)}${(o.icon !== null) ? '</div>' : ''}`,
      }));
    },
    internalCustomerPhone() {
      return this.customerPhone ? `${this.customerPhonePrefix} ${this.customerPhone}` : '';
    },
    internalSalesRepPhone() { // currently assume that prefix is the same
      return this.salesRepPhone ? `${this.customerPhonePrefix} ${this.salesRepPhone}` : '';
    },
    internalLeadPhone() { // currently assume that prefix is the same
      return this.leadPhone ? `${this.customerPhonePrefix} ${this.leadPhone}` : '';
    },
    internalLeadEmail() {
      return this.leadEmail ? this.leadEmail : '';
    },
    internalLeadPrivacyLink() {
      return this.leadPrivacyLink ? this.leadPrivacyLink : '';
    },
    statusFlowVisible() {
      return this.statusFlows.length > 1;
    },
    startDatePlaceholder() {
      if (this.isStartDateReadonly) {
        return this.$t('pos.settings.predicted-start-date', { date: this.predictedStartDate.toISOString() });
      }
      return this.$t('draft_field.placeholder.start-date');
    },
    notifyCustomer: {
      get() {
        return this.campaign.details.notifyCustomer || '';
      },
      set(value) {
        this.campaign.details = {
          ...this.campaign.details,
          notifyCustomer: value,
        };
        return value;
      },
    },
    appointed_onboarding: {
      get() {
        return this.campaign.details.appointed_onboarding || '';
      },
      set(value) {
        this.campaign.details = {
          ...this.campaign.details,
          appointed_onboarding: value,
        };
        return value;
      },
    },
    sameOffer: {
      get() {
        return this.campaign.offer_settings.same_offer || false;
      },
      set(value) {
        this.campaign.offer_settings = {
          ...this.campaign.offer_settings,
          same_offer: value,
        };
        return value;
      },
    },
    multilanguageEnabled() {
      return (this.availableLanguages.length > 1);
    },
    languageOptions() {
      return Object.values(this.availableLanguages).map(code => ({
        text: this.$t(`terms.language.name.${code}`),
        value: code,
      }));
    },
    isStartDateReadonly() {
      if (_.get(this.campaign, 'details.clone-of-start-date', null) === null &&
        this.campaign.flow === STATUS_FLOW_ASAP) {
        return true;
      }
      return false;
    },
    briefPlaceholder() {
      const objectiveSlug = _.get(this, 'campaign.objective', null);
      const overwriteKey = objectiveSlug !== null ? `terms.brief-description.${objectiveSlug}` : null;
      if (overwriteKey && this.$i18n.i18next.exists(overwriteKey)) {
        return this.$t(overwriteKey);
      }
      return this.$t('draft_field.placeholder.brief');
    },
    showSkipOnboarding() {
      const isDuplicate = _.get(this, 'campaign.details.duplicate_of_draft', null);
      const flow = this.statusFlows.find(f => f.tag === _.get(this, 'campaign.flow', null));

      return isDuplicate && flow && flow.creativeClassName === CREATIVE_FLOW_CLASS_ONBOARDING;
    },
    skipOnboarding: {
      get() {
        return this.campaign.details.skip_onboarding || false;
      },
      set(value) {
        this.campaign.details = {
          ...this.campaign.details,
          skip_onboarding: value,
        };
        return value;
      },
    },
  },
  mounted() {
    this.$on('keycombo::success', () => {
      this.fillTestData();
    });
    const fin = () => {
      debug.info('finished loading. error: %o', this.error);
      this.loading = false;
    };
    this.couponEnabled = this.couponCode !== '';
    this.selectedLanguage = this.campaign.customer.language;
    Promise.all([
      this.landingPageService.getDefault(), // must be 1st (for next .then)
      this.statusFlowService.getDefault(), // must be 2nd (for next .then)
      this.statusFlowService.getStatusFlows().then(res => this.statusFlows = res),
      this.statusesService.getFirstAvailableStartDate(
          this.campaign.flow,
          _.get(this.campaign, 'details.newcreative', false),
          _.get(this.campaign, 'details.clone-of-start-date', null))
        .then(minStart => this.minStart = minStart),
      this.landingPageService.getPageTypes().then(res => this.pageTypes = res),
      this.genericConfigService.getConfig().then(c => {
        this.agent_summary_groups = c.agent_summary_groups;
        this.feature_appointed_onboarding = c.feature_appointed_onboarding === '1';
        this.feature_upsells = c.feature_upsells === '1';
        this.fieldSettings = c.field_configuration || {};
        this.asapCalculationType = c.asap_calculation;
        this.subscriptionPeriodLength = getSubscriptionPeriodLength(c);
        this.subscriptionPeriodType = getSubscriptionPeriodType(c);
        this.maxStart = moment(c.max_start_date);

        this.availablePhonePrefixes = c.country_data
          .concat() // shallow clone to not mess up original order
          .sort((a, b) => {
            // aidx and bidx are position of this country in target locations
            // if country is not in target locations, set it's position to prefix*100
            // to fake something like 'ORDER BY selected_locations ASC, prefix ASC'

            let aidx = this.campaign.target.locations.findIndex(l => l.countryCode === a.value);
            let bidx = this.campaign.target.locations.findIndex(l => l.countryCode === b.value);

            aidx = (aidx < 0) ? parseInt(a.phone.prefix, 10) * 100 : aidx;
            bidx = (bidx < 0) ? parseInt(b.phone.prefix, 10) * 100 : bidx;

            return aidx - bidx;
          })
          .map(country => ({ prefix: country.phone.prefix }));

        if (this.availablePhonePrefixes.length > 0) {
          if (!this.availablePhonePrefixes.find(p => p.prefix === this.customerPhonePrefix)) {
            this.customerPhonePrefix = this.availablePhonePrefixes[0].prefix;
          }
        }

        const curGoalConfig = _.get(c, `goal_types.${this.campaign.goal_type}.config`, null);
        if (curGoalConfig && curGoalConfig.fbPageRequired) {
          if (!Array.isArray(this.fieldSettings.required)) {
            this.fieldSettings.required = [];
          }
          if (!Array.isArray(this.fieldSettings.hidden)) {
            this.fieldSettings.hidden = [];
          }
          if (!this.fieldSettings.required.includes('facebook.page')) {
            this.fieldSettings.required.push('facebook.page');
          }
          if (!this.fieldSettings.hidden.includes('facebook.hasFacebookPage')) {
            this.fieldSettings.hidden.push('facebook.hasFacebookPage');
          }
          this.campaign.facebook.hasFacebookPage = true;
        }

        this.lead_config = _.get(c, `goal_types.${GOAL_LEAD_GENERATION}.config`, null);
        if (c.goal_types && c.goal_types.local_offers) {
          this.offersCodeTypeOptions = [
            {
              text: this.$t('draft_field.offer_settings.code_type_options.empty'),
              value: 'empty',
            },
            ...c.goal_types.local_offers.code_types.map(l => ({
              text: this.$t(`draft_field.offer_settings.code_type_options.${l}`),
              value: l,
            })),
          ];
          this.offersRedeemOptions = [
            {
              text: this.$t('draft_field.offer_settings.redeem_options.empty'),
              value: 'empty',
            },
            ...c.goal_types.local_offers.redeems.map(l => ({
              text: this.$t(`draft_field.offer_settings.redeem_options.${l}`),
              value: l,
            })),
          ];

          if (c.goal_types.local_offers.code_types.length > 1 &&
            !c.goal_types.local_offers.code_types.includes(
            this.campaign.offer_settings.code_type)) {
            this.campaign.offer_settings.code_type = 'empty';
          }
          if (c.goal_types.local_offers.redeems.length > 1 &&
            !c.goal_types.local_offers.redeems.includes(
            this.campaign.offer_settings.redeem)) {
            this.campaign.offer_settings.redeem = 'empty';
          }
        }
      }),
      this.objectivesService.getObjectives().then(res => {
        this.objectives = res;
      }),
      this.localeService.getUserLanguage().then(language => {
        if (!this.selectedLanguage) {
          this.selectedLanguage = language;
        }
      }),
    ])
    .then(([defaultLandingPage, defaultFlow]) => {
      let idx = this.pageTypes.findIndex(pt => pt.value === this.campaign.landing_page.type);
      if (idx === -1) {
        this.campaign.landing_page.type = defaultLandingPage.value;
        this.$toast.warn({ message: this.$t('campaign.reset.landing-page-type') });
      }
      idx = this.statusFlows.findIndex(sf => sf.tag === this.campaign.flow);
      if (idx === -1) {
        this.campaign.flow = defaultFlow.tag;
        this.$toast.warn({ message: this.$t('campaign.reset.status-flow') });
      }

      if (this.isStartDateReadonly && this.campaign.start_date !== null) {
        this.campaign.start_date = null;
      } else if (_.get(this.campaign, 'details.clone-of-start-date', null) !== null &&
        _.get(this.campaign, 'start_date', null) !== null &&
        moment(this.campaign.start_date).isBefore(this.minStart)) {
        this.campaign.start_date = this.minStart.toISOString();
      }

      if (this.isFieldVisible('campaign.details.notify_customer') && !('notifyCustomer' in this.campaign.details)) {
        this.campaign.details = {
          ...this.campaign.details,
          notifyCustomer: this.fieldDefaultValue('campaign.details.notify_customer'),
        };
      }

      if (this.isFieldVisible('campaign.details.appointed_onboarding') && !('appointed_onboarding' in this.campaign.details) && this.showOnboardingField()) {
        this.campaign.details = {
          ...this.campaign.details,
          appointed_onboarding: this.fieldDefaultValue('campaign.details.appointed_onboarding'),
        };
      }

      if (!this.isFieldVisible('campaign.facebook_page.url')) {
        this.campaign.facebook.hasFacebookPage = false;
      }
    })
    .then(fin)
    .catch(e => { this.error = e; fin(); });
  },
  methods: {
    showModalObjectives() {
      this.objectiveTemp = this.campaign.objective ? this.campaign.objective : null;
      this.$refs.modal.show();
      if (this.$refs.modal.$refs.modal.classList) {
        this.$refs.modal.$refs.modal.classList.add('show');
      }
    },
    setObjective() {
      this.campaign.objective = this.objectiveTemp;
    },
    isPrivacyUrlIsValid(inputValue) {
      if (this.leadPrivacyPolicyLink() && !_.get(this.campaign, 'create_landing_page', false)) {
        return validateUrl(inputValue);
      }
      return true;
    },
    isFormGroupVisible(formGroupName) {
      const formGroup = _.get(this.formGroups, formGroupName, false);
      if (formGroup) {
        return Object.keys(formGroup).some(field => {
          if (_.get(formGroup, field, null) !== null) {
            return this.isFieldVisible(field) && _.get(formGroup, field);
          }
          return this.isFieldVisible(field);
        });
      }
      return false;
    },
    recursive(object, key) {
      Object.keys(object).forEach(k => {
        const dotNotation = key === '' ? k : `${key}.${k}`;
        if (object[k] !== null && typeof object[k] === 'object') {
          this.recursive(object[k], dotNotation);
        }
        if (Object.prototype.hasOwnProperty.call(this.fieldSettings, 'required')
            && this.fieldSettings.required.findIndex(elem => elem === dotNotation) >= 0) {
          object[k].required = required;
        }
        if (Object.prototype.hasOwnProperty.call(this.fieldSettings, 'numeric')
            && this.fieldSettings.numeric.findIndex(elem => elem === dotNotation) >= 0) {
          object[k].numeric = numeric;
        }
        if (Object.prototype.hasOwnProperty.call(this.fieldSettings, 'field_size')) {
          const elConfig = _.get(this.fieldSettings.field_size, dotNotation, null);
          if (elConfig) {
            if (typeof elConfig.max !== 'undefined') {
              object[k].maxLength = maxLength(elConfig.max);
            }
            if (typeof elConfig.min !== 'undefined') {
              object[k].minLength = minLength(elConfig.min);
            }
          }
        }
        if (Object.prototype.hasOwnProperty.call(this.fieldSettings, 'expression')) {
          const elConfig = _.get(this.fieldSettings.expression, dotNotation, null);
          if (elConfig) {
            if (typeof elConfig.replaceResExp !== 'undefined' && typeof elConfig.replaceResTemplate !== 'undefined') {
              this.replaceFieldExpressions[dotNotation] = {
                exp: elConfig.replaceResExp,
                resTemplate: elConfig.replaceResTemplate,
              };
            }
            if (typeof elConfig.expr !== 'undefined' && typeof elConfig.type !== 'undefined') {
              const paramData = { max: 0 };
              if (elConfig.type === 'maxLength') {
                paramData.type = 'EMPTY_ERROR';
              }
              object[k].expression = withParams(paramData, (value) => {
                if (elConfig.maxLen !== 'undefined') {
                  paramData.max = elConfig.maxLen;
                }
                if (elConfig.maxLenExprAdd !== 'undefined' && value) {
                  const matches = value.match(elConfig.maxLenExprAdd);
                  paramData.max += (matches ? matches[0].length : 0);
                }
                if (paramData.max > 0) {
                  object[k].maxLength = maxLength(paramData.max);
                }
                return Boolean(!value || value.match(elConfig.expr));
              });
            }
          }
        }
      });
    },
    overwriteFieldValues() {
      Object.keys(this.replaceFieldExpressions).forEach(k => {
        const curValue = _.get(this.campaign, k, null);
        if (curValue !== null) {
          let newValue = curValue.match(this.replaceFieldExpressions[k].exp);
          if (newValue) {
            newValue = this.replaceFieldExpressions[k].resTemplate.replace('[replaceResExp]', newValue);
            _.set(this.campaign, k, newValue);
          }
        }
      });
    },
    showOnboardingField() {
      return this.feature_appointed_onboarding;
    },
    toNextStep() {
      this.invalidUrl = false;
      this.overwriteFieldValues();
      this.campaign.currentStep = STEP_SETTINGS;
      this.draftService.updateDraft(this.$route.params.id, this.campaign)
          .then(id => {
            this.$router.push({
              name: STEP_CHECKOUT,
              params: { id },
            });
          });
    },
    save() {
      if (this.$v.$invalid) {
        this.$v.$touch();
        if (this.$v.campaign.offer_settings.$invalid) {
          this.$toast.error({ message: this.$t('error.check-offer-fields') });
        }
        if (this.notModalValuesAreInvalid()) {
          this.$toast.error({ message: this.$t('error.check-all-fields') });
        }
      } else {
        if (this.campaign.landing_page.url in this.validationUrls) {
          if (this.validationUrls[this.campaign.landing_page.url] === true ||
            this.campaign.create_landing_page
          ) {
            this.toNextStep();
          } else {
            this.invalidUrl = true;
            this.$toast.error({ message: this.$t('error.check-all-fields') });
          }
        }
        if (!(this.campaign.landing_page.url in this.validationUrls)) {
          if (this.campaign.create_landing_page) {
            this.toNextStep();
          } else if (this.isPostEngagement) {
            this.toNextStep();
          } else {
            this.adApiService.validateUrl(this.campaign.landing_page.url)
              .then(result => {
                this.validationUrls[this.campaign.landing_page.url] = result;
                if (result) {
                  this.toNextStep();
                } else {
                  this.invalidUrl = true;
                  this.$toast.error({ message: this.$t('error.check-all-fields') });
                }
              });
          }
        }
      }
    },
    notModalValuesAreInvalid() {
      let valid = false;
      Object.keys(this.$v.campaign).forEach(key => {
        if (key !== 'offer_settings' && this.$v.campaign[key].$invalid === true) {
          valid = true;
        }
      });
      return valid;
    },
    onChangeEventHandler(value) {
      if (value) {
        this.campaign.flow = STATUS_FLOW_ASAP;
        this.campaign.start_date = null;
      } else {
        this.campaign.flow = STATUS_FLOW_DEFAULT;
      }
      this.statusesService.getFirstAvailableStartDate(
          this.campaign.flow,
          _.get(this.campaign, 'details.newcreative', false),
          _.get(this.campaign, 'details.clone-of-start-date', null))
        .then(minStart => this.minStart = minStart);
    },
    onChangeCouponEventHandler(value) {
      this.couponEnabled = value;
      if (!value) {
        this.couponCode = '';
      }
    },
    dateInput(value) {
      this.campaign.start_date = new Date(value).toISOString();
      this.$v.campaign.start_date.$touch();
    },
    onChangeDisableLandingPage(value) {
      if (value) {
        this.campaign.landing_page.type = 'webpage';
        this.campaign.landing_page.url = null;
      }
    },
    onChangeDisableSameOffer(value) {
      if (value) {
        this.campaign.offer_settings.same_offer = value;
      } else {
        this.campaign.offer_settings.same_offer = false;
      }
    },
    isFieldVisible(fieldName) {
      return !Object.prototype.hasOwnProperty.call(this.fieldSettings, 'hidden')
              || this.fieldSettings.hidden.findIndex(f => `campaign.${f}` === fieldName) === -1;
    },
    isFieldReadOnly(fieldName) {
      return _.get(this.campaign, 'prefilled.readonly', []).indexOf(fieldName) >= 0;
    },
    fieldDefaultValue(fieldName) {
      let value = null;

      if (Object.prototype.hasOwnProperty.call(this.fieldSettings, 'default')) {
        Object.keys(this.fieldSettings.default).forEach((key) => {
          if (`campaign.${key}` === fieldName) {
            value = this.fieldSettings.default[key];
          }
        });
      }

      return value;
    },
    calculateBackgroundPosition(event) {
      event.target.style.backgroundPositionY = `-${event.target.scrollTop + 1}px`;
    },
    fillTestData() {
      debug.info('Filling Draft with test data');
      this.campaign.name = `Test campaign #${this.campaign.id}`;
      this.campaign.customer.name = `Test company name #${this.campaign.id}`;
      this.campaign.customer.email = 'test@test.com';
      this.customerPhone = '12345678';
      this.selectedLanguage =
        this.availableLanguages[Math.floor(Math.random() * this.availableLanguages.length)];
      this.campaign.start_date = this.predictedStartDate.toISOString();
      const elements = this.$refs.fbPageCreator.$el
        .getElementsByClassName('custom-control form-check-inline custom-radio');
      elements[elements.length - 1].click();
      this.campaign.facebook.hasFacebookPage = false;
      this.campaign.landing_page.url = 'test.com';
      this.campaign.brief = `Test campaign #${this.campaign.id} brief description`;
      this.campaign.objective = this.availableObjectives[
        Math.floor((Math.random() * (this.objectives.length - 1)) + 1)
      ].text;
      if (this.localOffersEnabled) {
        this.campaign.offer_settings.code_type = this.offersCodeTypeOptions[1].value;
        this.campaign.offer_settings.redeem = this.offersRedeemOptions[1].value;
      }
      if (this.leadPrivacyPolicyLink()) {
        this.campaign.details.leadPrivacyLink = 'https://www.example.com/privacy-policy';
      }
    },
    validatePhoneMaxLength(max) {
      return withParams({ type: 'maxLength', max }, () => this.customerPhone.length <= max);
    },
    leadGenerationEnabled() {
      return this.campaign.goal_type === GOAL_LEAD_GENERATION;
    },
    leadQuestionVisible() {
      if (this.leadGenerationEnabled() && this.lead_config) {
        return !this.lead_config.default_question_disabled;
      }
      return false;
    },
    leadPrivacyPolicyLink() {
      if (
        this.leadGenerationEnabled()
        && _.get(this.lead_config, 'custom_privacy_policy_link', false) === true
      ) {
        return true;
      }
      return false;
    },
    showModalOfferSettings() {
      this.offerSettingSaved = false;
      this.offerModalErrors.url = [];
      this.offerModalErrors.code_type = false;
      this.offerModalErrors.redeem_options = false;
      this.currentOfferSettings = _.cloneDeep(this.campaign.offer_settings);
      this.$refs.modalOfferSettings.show();
      // added because sometimes show() bugs after multiple open/close on Chrome
      if (this.$refs.modalOfferSettings.$refs.modal.classList) {
        this.$refs.modalOfferSettings.$refs.modal.classList.add('show');
      }
      // update remaining character counts
      this.$refs.modalOfferSettings.$children[0].$children.forEach(component => {
        if (component.updateCharCounterValue) {
          component.updateCharCounterValue();
        }
      });
    },
    checkCodeVisible() {
      if (this.offerModalErrors) {
        this.offerModalErrors.code_type = this.campaign.offer_settings.code_type === 'empty';
      }

      if (this.campaign.offer_settings.code_type === 'one') {
        this.offerCodeVisible = true;
      } else {
        this.offerCodeVisible = false;
      }
      return this.offerCodeVisible;
    },
    checkUniqueCode() {
      const code = this.campaign.offer_settings.code;
      if (code !== '') {
        this.offerModalErrors.unique_code = false;
      }
    },
    checkRedeemUrlVisible() {
      if (this.offerModalErrors) {
        this.offerModalErrors.redeem_options = this.campaign.offer_settings.redeem === 'empty';
      }

      if (this.campaign.offer_settings.redeem === 'online' ||
        this.campaign.offer_settings.redeem === 'both') {
        this.offerRedeemUrlVisible = true;
      } else {
        this.offerRedeemUrlVisible = false;
      }
      return this.offerRedeemUrlVisible;
    },
    hideOfferSettingsModal() {
      this.$refs.modalOfferSettings.hide();
    },
    hiddenOfferSettings() {
      if (this.offerSettingSaved === false) {
        this.campaign.offer_settings = _.cloneDeep(this.currentOfferSettings);
      }
    },
    saveOfferSettings() {
      let hideModal = true;
      const offerSettings = this.campaign.offer_settings;
      if (offerSettings.code_type === 'empty') {
        hideModal = false;
        this.offerModalErrors.code_type = true;
      }
      if (offerSettings.redeem === 'empty') {
        hideModal = false;
        this.offerModalErrors.redeem_options = true;
      }
      if ((offerSettings.code === '' || offerSettings.code == null) && offerSettings.code_type === 'one') {
        hideModal = false;
        this.offerModalErrors.unique_code = true;
      }
      if (!this.checkCodeVisible()) {
        this.campaign.offer_settings.code = '';
      }
      if (!this.checkRedeemUrlVisible()) {
        this.campaign.offer_settings.offer_url = '';
      }
      if (this.checkRedeemUrlVisible()) {
        if (!validateUrl(this.campaign.offer_settings.offer_url)) {
          this.offerModalErrors.url = ['isUrl'];
          hideModal = false;
        }
      }

      if (hideModal) {
        this.offerModalErrors.url = [];
        this.offerModalErrors.code_type = false;
        this.offerModalErrors.redeem_options = false;
        this.offerSettingSaved = true;
        this.hideOfferSettingsModal();
      }
    },
    disabledMonthDays(date) {
      const subscriptionStartDate = _.get(this.campaign, 'details.clone-of-start-date', null);
      if (subscriptionStartDate !== null) {
        return !isIterationDate(localDateToApi(date),
          this.subscriptionPeriodType, this.subscriptionPeriodLength,
          moment(subscriptionStartDate));
      }
      return false;
    },
  },
  watch: {
    'campaign.customer.email': function watchCampaignCustomerEmail(after) {
      this.campaign.facebook.email = after;
      if (this.leadGenerationEnabled()) {
        this.leadEmail = after;
      }
    },
    'campaign.facebook.hasFacebookPage': function watchCampaignCustomerEmail(after) {
      if (!after) {
        this.campaign.landing_page.type = 'webpage';
        this.campaign.facebook.page = null; // Remove selected facebook page
      }
    },
    'campaign.landing_page.type': function watchCampaignLandingPageType(after) {
      if (after === PAGE_FACEBOOK && this.campaign.facebook.page) {
        this.campaign.landing_page.url = this.campaign.facebook.page.link;
      }
    },
    selectedLanguage: function watchSelectedLanguage(after) {
      this.campaign.customer.language = after;
    },
    internalCustomerPhone: function watchInternalCustomerPhone(after) {
      this.campaign.customer.phone = after;

      if (this.leadGenerationEnabled()) {
        this.leadPhone = after ? after.split(' ').slice(1).join(' ') : '';
      }
    },
    internalSalesRepPhone: function watchInternalSalesRepPhone(after) {
      const newDetails = {
        ...this.campaign.details,
        salesRepPhone: after,
      };
      if (!after) {
        delete newDetails.salesRepPhone;
      }
      this.campaign.details = newDetails;
    },
    internalLeadPhone: function watchInternalLeadPhone(after) {
      const newDetails = {
        ...this.campaign.details,
        leadPhone: after,
      };
      if (!after) {
        delete newDetails.leadPhone;
      }
      this.campaign.details = newDetails;
    },
    internalLeadEmail: function watchInternalLeadEmail(after) {
      const newDetails = {
        ...this.campaign.details,
        leadEmail: after,
      };
      if (!after) {
        delete newDetails.leadEmail;
      }
      this.campaign.details = newDetails;
    },
    internalLeadPrivacyLink: function watchInternalLeadEmail(after) {
      const newDetails = {
        ...this.campaign.details,
        leadPrivacyLink: after,
      };
      if (!after) {
        delete newDetails.leadPrivacyLink;
      }
      this.campaign.details = newDetails;
    },
  },
};



// WEBPACK FOOTER //
// ./src/components/campaign-settings/CampaignSettings.js